// This file contains the logic to process patient data

// Process patient data to extract fields required for AICVD model
export function processPatientData(patientData) {
  const mappedData = {}
  const mappingPaths = {}

  // Extract basic information
  mappedData.AHCno = extractAHCNumber(patientData, mappingPaths)
  mappedData.Id = extractPatientID(patientData, mappingPaths)
  mappedData.Name = extractName(patientData, mappingPaths)
  mappedData.Age = extractAge(patientData, mappingPaths)
  mappedData.Gender = extractGender(patientData, mappingPaths)

  // Extract physical measurements
  mappedData.Height = extractHeight(patientData, mappingPaths)
  mappedData.Weight = extractWeight(patientData, mappingPaths)
  mappedData.BMI = extractBMI(patientData, mappingPaths)
  mappedData.BloodPressureSystolic = extractSystolic(patientData, mappingPaths)
  mappedData.BloodPressureDiastolic = extractDiastolic(patientData, mappingPaths)
  mappedData.HeartRatePerMinute = extractHeartRate(patientData, mappingPaths)
  mappedData.RespirationRatePerMinute = extractRespirationRate(patientData, mappingPaths)
  mappedData.Rhythm = extractPulseRhythm(patientData, mappingPaths)

  // Extract lifestyle factors
  mappedData.PhysicalActivity = extractPhysicalActivity(patientData, mappingPaths)
  mappedData.Smoke = extractSmoking(patientData, mappingPaths)
  mappedData.Tobacco = extractTobacco(patientData, mappingPaths)
  mappedData.Diet = extractDiet(patientData, mappingPaths)
  mappedData.Alcohol = extractAlcohol(patientData, mappingPaths)

  // Add this line after the lifestyle factors section in the processPatientData function
  mappedData.PsychologicalStress = extractPsychologicalStress(patientData, mappingPaths)

  // Extract medical conditions
  mappedData.Hypertension = extractHypertension(patientData, mappingPaths)
  mappedData.HeartRelatedSymptom = extractHeartRelatedSymptom(patientData, mappingPaths)
  mappedData.PrevHeartDisease = extractPrevHeartDisease(patientData, mappingPaths)
  mappedData.FamilyHistoryofHeartDisease = extractFamilyHistoryofHeartDisease(patientData, mappingPaths)
  mappedData.DiabetesMellitus = extractDiabetes(patientData, mappingPaths)
  mappedData.Dyslipidaemia = extractDyslipidaemia(patientData, mappingPaths)

  // Create AICVD model data for submission to API
  const aicvdModelData = createAICVDModelData(mappedData, patientData)

  // Save the extracted values as a simple key-value object
  const extractedValues = {
    Id: mappedData.Id || patientData.patient_id || "P123",
    Age: mappedData.Age || 55,
    Gender: mappedData.Gender || "Male",
    DateOfVisit: patientData.DateOfVisit || new Date().toISOString().split("T")[0],
    CenterId: patientData.CenterId || "A1",
    BMI: mappedData.BMI || 33,
    Height: mappedData.Height || 165,
    Weight: mappedData.Weight || 90,
    BloodPressureDiastolic: mappedData.BloodPressureDiastolic || 80,
    BloodPressureSystolic: mappedData.BloodPressureSystolic || 115,
    Rhythm: mappedData.Rhythm || "Regularly Regular",
    HeartRatePerMinute: mappedData.HeartRatePerMinute || 72,
    RateofRespirationPerMinute: mappedData.RespirationRatePerMinute || 22,
    HeartRelatedSymptom: mappedData.HeartRelatedSymptom || "No",
    PsychologicalStress: mappedData.PsychologicalStress || "Anxiety Syndromes",
    PhysicalActivity: mappedData.PhysicalActivity || "Sedentary",
    Smoke: mappedData.Smoke || "No",
    Tobacco: mappedData.Tobacco || "Yes",
    Diet: mappedData.Diet || "Non-Veg",
    Alcohol: mappedData.Alcohol || "No",
    DiabetesMellitus: mappedData.DiabetesMellitus || "Not taking medication",
    FamilyHistoryofHeartDisease: mappedData.FamilyHistoryofHeartDisease || "Yes",
    Hypertension: mappedData.Hypertension || "Yes",
    Dyslipidaemia: mappedData.Dyslipidaemia || "No",
    PrevHeartDisease: mappedData.PrevHeartDisease || "Yes",
  }

  // Make sure to return all objects including the new extractedValues
  return { mappedData, mappingPaths, aicvdModelData, extractedValues }
}

// Create data in the format expected by the AICVD model
function createAICVDModelData(mappedData, patientData) {
  // Map the data to the exact format expected by the AICVD model
  return {
    // Basic patient information
    Id: mappedData.Id || patientData.patient_id || "P123",
    Age: mappedData.Age || 55,
    Gender: mappedData.Gender || "Male",
    DateOfVisit: mappedData.DateOfVisit || new Date().toISOString().split("T")[0],
    CenterId: mappedData.CenterId || "A1",

    // Physical measurements
    BMI: mappedData.BMI || 33,
    Height: mappedData.Height || 165,
    Weight: mappedData.Weight || 90,
    BloodPressureDiastolic: mappedData.BloodPressureDiastolic || 80,
    BloodPressureSystolic: mappedData.BloodPressureSystolic || 115,
    Rhythm: mappedData.Rhythm || "Regularly Regular",
    HeartRatePerMinute: mappedData.HeartRatePerMinute || 72,
    RateofRespirationPerMinute: mappedData.RespirationRatePerMinute || 22,

    // Medical conditions
    HeartRelatedSymptom: mappedData.HeartRelatedSymptom || "No",
    PsychologicalStress: mappedData.PsychologicalStress || "Anxiety Syndromes",
    PhysicalActivity: mappedData.PhysicalActivity || "Sedentary",
    Smoke: mappedData.Smoke || "No",
    Tobacco: mappedData.Tobacco || "Yes",
    Diet: mappedData.Diet || "Non-Veg",
    Alcohol: mappedData.Alcohol || "No",
    DiabetesMellitus: mappedData.DiabetesMellitus || "Not taking medication",
    FamilyHistoryofHeartDisease: mappedData.FamilyHistoryofHeartDisease || "Yes",
    Hypertension: mappedData.Hypertension || "Yes",
    Dyslipidaemia: mappedData.Dyslipidaemia || "No",
    PrevHeartDisease: mappedData.PrevHeartDisease || "Yes",
  }
}



function getStringValue(value) {
  if (value === null || value === undefined) return ""

  // If it's already a string, return it
  if (typeof value === "string") return value

  // If it's an object with a note property, return the note
  if (typeof value === "object" && value !== null && value.note) return String(value.note)

  // If it's an object with a value property, return the value
  if (typeof value === "object" && value !== null && value.value) return String(value.value)

  // If it's an object with a result property, return the result
  if (typeof value === "object" && value !== null && value.result) return String(value.result)

  // If it's an object with a status property, return the status
  if (typeof value === "object" && value !== null && value.status) return String(value.status)

  // If it's an object with an interpretation property, return the interpretation
  if (typeof value === "object" && value !== null && value.interpretation) return String(value.interpretation)

  // Try to convert to string
  try {
    return String(value)
  } catch (e) {
    return ""
  }
}


// Helper functions to extract specific fields

function extractAHCNumber(patientData, mappingPaths) {
  mappingPaths.AHCno = "patient_id"
  return patientData.patient_id || null
}

function extractPatientID(patientData, mappingPaths) {
  mappingPaths.Id = "patient_id"
  return patientData.patient_id || null
}

function extractName(patientData, mappingPaths) {
  mappingPaths.Name = "name"
  return patientData.name || null
}

function extractAge(patientData, mappingPaths) {
  // Check locations in sequential order as per Epic system
  // 1. Demographics Section
  if (patientData.demographics_section?.age) {
    mappingPaths.Age = "demographics_section>age"
    return patientData.demographics_section.age
  }

  // 2. Patient Banner/Header
  if (patientData.patient_banner_header?.age) {
    mappingPaths.Age = "patient_banner_header>age"
    return patientData.patient_banner_header.age
  }

  // 3. Encounter Information/Visit Details
  if (patientData.encounter_information?.age) {
    mappingPaths.Age = "encounter_information>age"
    return patientData.encounter_information.age
  }

  // 4. Clinical Documentation/Progress Notes
  if (patientData.clinical_documentation?.age) {
    mappingPaths.Age = "clinical_documentation>age"
    return patientData.clinical_documentation.age
  }

  if (patientData.progress_notes?.age) {
    mappingPaths.Age = "progress_notes>age"
    return patientData.progress_notes.age
  }

  // Calculate age from DOB if available
  if (patientData.dob) {
    mappingPaths.Age = "calculated from dob"
    const birthDate = new Date(patientData.dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  return null
}

function extractGender(patientData, mappingPaths) {
  // Check locations in sequential order as per Epic system
  // 1. Demographics Section
  if (patientData.demographics_section?.gender) {
    mappingPaths.Gender = "demographics_section>gender"
    return patientData.demographics_section.gender
  }

  // 2. Patient Banner/Header
  if (patientData.patient_banner_header?.gender) {
    mappingPaths.Gender = "patient_banner_header>gender"
    return patientData.patient_banner_header.gender
  }

  // 3. Registration Section
  if (patientData.registration_section?.gender) {
    mappingPaths.Gender = "registration_section>gender"
    return patientData.registration_section.gender
  }

  // 4. Clinical Documentation/Progress Notes
  if (patientData.clinical_documentation?.gender) {
    mappingPaths.Gender = "clinical_documentation>gender"
    return patientData.clinical_documentation.gender
  }

  if (patientData.progress_notes?.gender) {
    mappingPaths.Gender = "progress_notes>gender"
    return patientData.progress_notes.gender
  }

  // 5. Encounter Information/Visit Details
  if (patientData.encounter_information?.gender) {
    mappingPaths.Gender = "encounter_information>gender"
    return patientData.encounter_information.gender
  }

  return null
}

function extractHeight(patientData, mappingPaths) {
  // Check in vital signs first
  if (patientData.vital_signs?.height) {
    mappingPaths.Height = "vital_signs>height"
    return patientData.vital_signs.height
  }

  // Then check in flowsheets
  if (patientData.flowsheets?.height) {
    mappingPaths.Height = "flowsheets>height"
    // Handle both string and object formats
    if (typeof patientData.flowsheets.height === "object" && patientData.flowsheets.height.value) {
      return patientData.flowsheets.height.value
    }
    return patientData.flowsheets.height
  }

  return null
}

function extractWeight(patientData, mappingPaths) {
  // Check in vital signs first
  if (patientData.vital_signs?.weight) {
    mappingPaths.Weight = "vital_signs>weight"
    return patientData.vital_signs.weight
  }

  // Then check in flowsheets
  if (patientData.flowsheets?.weight) {
    mappingPaths.Weight = "flowsheets>weight"
    // Handle both string and object formats
    if (typeof patientData.flowsheets.weight === "object" && patientData.flowsheets.weight.value) {
      return patientData.flowsheets.weight.value
    }
    return patientData.flowsheets.weight
  }

  return null
}

function extractBMI(patientData, mappingPaths) {
  // Check locations in sequential order as per Epic system
  // 1. Vital Signs
  if (patientData.vital_signs?.bmi) {
    mappingPaths.BMI = "vital_signs>bmi"
    return patientData.vital_signs.bmi
  }

  // 2. Patient Summary
  if (patientData.patient_summary?.bmi) {
    mappingPaths.BMI = "patient_summary>bmi"
    return patientData.patient_summary.bmi
  }

  // 3. Growth Charts
  if (patientData.growth_charts?.bmi) {
    mappingPaths.BMI = "growth_charts>bmi"
    return patientData.growth_charts.bmi
  }

  // 4. Encounter or Visit Information
  if (patientData.encounter_information?.bmi) {
    mappingPaths.BMI = "encounter_information>bmi"
    return patientData.encounter_information.bmi
  }

  // 5. Health Maintenance
  if (patientData.health_maintenance?.bmi) {
    mappingPaths.BMI = "health_maintenance>bmi"
    return patientData.health_maintenance.bmi
  }

  // 6. Flowsheets
  if (patientData.flowsheets?.bmi) {
    mappingPaths.BMI = "flowsheets>bmi"
    return patientData.flowsheets.bmi
  }

  // Calculate BMI if height and weight are available
  const height = extractHeight(patientData, {})
  const weight = extractWeight(patientData, {})

  if (height && weight) {
    mappingPaths.BMI = "calculated from height and weight"

    // Extract numeric values
    const heightStr = height.toString()
    const weightStr = weight.toString()

    const heightMatch = heightStr.match(/(\d+(\.\d+)?)/)
    const weightMatch = weightStr.match(/(\d+(\.\d+)?)/)

    if (heightMatch && weightMatch) {
      const heightCm = Number.parseFloat(heightMatch[1])
      const weightKg = Number.parseFloat(weightMatch[1])

      if (heightCm && weightKg) {
        const heightM = heightCm / 100
        const bmi = weightKg / (heightM * heightM)
        return bmi.toFixed(2)
      }
    }
  }

  return null
}

function extractSystolic(patientData, mappingPaths) {
  // Check locations in sequential order as per Epic system
  // 1. Vital Signs Section
  if (patientData.vital_signs?.systolic_blood_pressure) {
    mappingPaths.BloodPressureSystolic = "vital_signs>systolic_blood_pressure"
    return patientData.vital_signs.systolic_blood_pressure
  }

  // 2. Flowsheets
  if (patientData.flowsheets?.systolic_blood_pressure) {
    mappingPaths.BloodPressureSystolic = "flowsheets>systolic_blood_pressure"
    return patientData.flowsheets.systolic_blood_pressure
  }

  // Try to extract from blood_pressure object
  if (patientData.lab_results?.blood_pressure?.systolic) {
    mappingPaths.BloodPressureSystolic = "lab_results>blood_pressure>systolic"
    return patientData.lab_results.blood_pressure.systolic
  }

  // Try to extract from blood_pressure string (e.g., "120/80 mmHg")
  if (patientData.lab_results?.blood_pressure) {
    if (typeof patientData.lab_results.blood_pressure === "string") {
      const bpMatch = patientData.lab_results.blood_pressure.match(/(\d+)\/(\d+)/)
      if (bpMatch && bpMatch[1]) {
        mappingPaths.BloodPressureSystolic = "lab_results>blood_pressure (parsed)"
        return Number.parseInt(bpMatch[1])
      }
    }
  }

  return null
}

function extractDiastolic(patientData, mappingPaths) {
  // Check locations in sequential order as per Epic system
  // 1. Vital Signs Section
  if (patientData.vital_signs?.diastolic_blood_pressure) {
    mappingPaths.BloodPressureDiastolic = "vital_signs>diastolic_blood_pressure"
    return patientData.vital_signs.diastolic_blood_pressure
  }

  // 2. Flowsheets
  if (patientData.flowsheets?.diastolic_blood_pressure) {
    mappingPaths.BloodPressureDiastolic = "flowsheets>diastolic_blood_pressure"
    return patientData.flowsheets.diastolic_blood_pressure
  }

  // Try to extract from blood_pressure object
  if (patientData.lab_results?.blood_pressure?.diastolic) {
    mappingPaths.BloodPressureDiastolic = "lab_results>blood_pressure>diastolic"
    return patientData.lab_results.blood_pressure.diastolic
  }

  // Try to extract from blood_pressure string (e.g., "120/80 mmHg")
  if (patientData.lab_results?.blood_pressure) {
    if (typeof patientData.lab_results.blood_pressure === "string") {
      const bpMatch = patientData.lab_results.blood_pressure.match(/(\d+)\/(\d+)/)
      if (bpMatch && bpMatch[2]) {
        mappingPaths.BloodPressureDiastolic = "lab_results>blood_pressure (parsed)"
        return Number.parseInt(bpMatch[2])
      }
    }
  }

  return null
}

function extractHeartRate(patientData, mappingPaths) {
  // Check locations in sequential order
  // 1. Vital Signs Section
  if (patientData.vital_signs?.heart_rate) {
    mappingPaths.HeartRatePerMinute = "vital_signs>heart_rate"
    return patientData.vital_signs.heart_rate
  }

  if (patientData.vital_signs?.pulse_rate) {
    mappingPaths.HeartRatePerMinute = "vital_signs>pulse_rate"
    return patientData.vital_signs.pulse_rate
  }

  // 2. Flowsheets
  if (patientData.flowsheets?.heart_rate) {
    mappingPaths.HeartRatePerMinute = "flowsheets>heart_rate"
    return patientData.flowsheets.heart_rate
  }

  if (patientData.flowsheets?.pulse_rate) {
    mappingPaths.HeartRatePerMinute = "flowsheets>pulse_rate"
    return patientData.flowsheets.pulse_rate
  }

  return null
}

function extractRespirationRate(patientData, mappingPaths) {
  // Check locations in sequential order (Breathing Rate is Respiration Rate)
  // 1. Vital Signs Section
  if (patientData.vital_signs?.respiration_rate) {
    mappingPaths.RespirationRatePerMinute = "vital_signs>respiration_rate"
    return patientData.vital_signs.respiration_rate
  }

  if (patientData.vital_signs?.breathing_rate) {
    mappingPaths.RespirationRatePerMinute = "vital_signs>breathing_rate"
    return patientData.vital_signs.breathing_rate
  }

  // 2. Flowsheets
  if (patientData.flowsheets?.respiration_rate) {
    mappingPaths.RespirationRatePerMinute = "flowsheets>respiration_rate"
    return patientData.flowsheets.respiration_rate
  }

  if (patientData.flowsheets?.breathing_rate) {
    mappingPaths.RespirationRatePerMinute = "flowsheets>breathing_rate"
    return patientData.flowsheets.breathing_rate
  }

  return null
}

// Completely rewritten extractPulseRhythm function to handle all possible data formats
function extractPulseRhythm(patientData, mappingPaths) {
  try {
    // 1. Check ECG Reports section (highest priority)
    if (patientData.ecg_reports?.rhythm) {
      mappingPaths.Rhythm = "ecg_reports>rhythm"
      return String(patientData.ecg_reports.rhythm)
    }

    // 2. Check Cardiology Module
    if (patientData.cardiology_module?.heart_rhythm) {
      mappingPaths.Rhythm = "cardiology_module>heart_rhythm"
      return String(patientData.cardiology_module.heart_rhythm)
    }

    if (patientData.cardiology_module?.ecg_rhythm) {
      mappingPaths.Rhythm = "cardiology_module>ecg_rhythm"
      return String(patientData.cardiology_module.ecg_rhythm)
    }

    // 3. Check Clinical Notes
    if (patientData.clinical_notes?.heart_rhythm) {
      mappingPaths.Rhythm = "clinical_notes>heart_rhythm"
      const value = patientData.clinical_notes.heart_rhythm

      // Handle object with note property
      if (value && typeof value === "object" && value.note) {
        return String(value.note)
      }

      // Handle direct string value
      if (value && typeof value === "string") {
        return value
      }

      // Handle any other type by converting to string
      if (value !== null && value !== undefined) {
        return String(value)
      }
    }

    if (patientData.clinical_notes?.ecg) {
      mappingPaths.Rhythm = "clinical_notes>ecg"
      const value = patientData.clinical_notes.ecg

      // Handle object with note property
      if (value && typeof value === "object" && value.note) {
        return String(value.note)
      }

      // Handle direct string value
      if (value && typeof value === "string") {
        return value
      }

      // Handle any other type by converting to string
      if (value !== null && value !== undefined) {
        return String(value)
      }
    }

    // 4. Check Vital Signs
    if (patientData.vital_signs?.heart_rhythm) {
      mappingPaths.Rhythm = "vital_signs>heart_rhythm"
      return String(patientData.vital_signs.heart_rhythm)
    }

    // 5. Check for rhythm information in ECG interpretation
    if (patientData.clinical_notes?.ecg?.interpretation) {
      mappingPaths.Rhythm = "clinical_notes>ecg>interpretation"
      return String(patientData.clinical_notes.ecg.interpretation)
    }

    // 6. Check for rhythm information in cardiology notes
    if (patientData.clinical_notes?.cardiology) {
      const cardioValue = patientData.clinical_notes.cardiology
      let cardioNote = ""

      // Handle object with note property
      if (cardioValue && typeof cardioValue === "object" && cardioValue.note) {
        cardioNote = String(cardioValue.note).toLowerCase()
      }
      // Handle direct string value
      else if (cardioValue && typeof cardioValue === "string") {
        cardioNote = cardioValue.toLowerCase()
      }
      // Handle any other type by converting to string
      else if (cardioValue !== null && cardioValue !== undefined) {
        cardioNote = String(cardioValue).toLowerCase()
      }

      if (cardioNote && cardioNote.includes("rhythm")) {
        mappingPaths.Rhythm = "clinical_notes>cardiology>note (extracted)"

        // Extract rhythm information using regex
        const rhythmMatch = cardioNote.match(/rhythm[:\s]*([\w\s]+)[.,]/i)
        if (rhythmMatch && rhythmMatch[1]) {
          return capitalizeFirstLetters(rhythmMatch[1].trim())
        }
      }
    }

    // 7. Search for rhythm-related keywords in all notes
    const rhythmKeywords = [
      "sinus rhythm",
      "normal sinus rhythm",
      "nsr",
      "atrial fibrillation",
      "afib",
      "af",
      "atrial flutter",
      "bradycardia",
      "tachycardia",
      "av block",
      "heart block",
      "mobitz",
      "complete heart block",
      "ventricular tachycardia",
      "premature ventricular contractions",
      "pvc",
      "regularly regular",
      "irregularly irregular",
    ]

    // Check all sections for rhythm-related terms
    for (const section in patientData) {
      if (typeof patientData[section] === "object" && patientData[section] !== null) {
        for (const key in patientData[section]) {
          const value = patientData[section][key]

          // Skip if value is null or undefined
          if (value === null || value === undefined) {
            continue
          }

          let valueStr = ""

          // Handle object with note property
          if (typeof value === "object" && value.note) {
            valueStr = String(value.note)
          }
          // Handle direct string value
          else if (typeof value === "string") {
            valueStr = value
          }
          // Handle any other type by converting to string
          else {
            try {
              valueStr = String(value)
            } catch (e) {
              // If conversion fails, skip this value
              continue
            }
          }

          if (valueStr) {
            const lowerValue = valueStr.toLowerCase()

            for (const keyword of rhythmKeywords) {
              if (lowerValue.includes(keyword)) {
                mappingPaths.Rhythm = `${section}>${key} (keyword: ${keyword})`

                // Map common rhythm patterns to standardized values
                if (keyword === "normal sinus rhythm" || keyword === "nsr") {
                  return "Normal Sinus Rhythm"
                } else if (keyword === "atrial fibrillation" || keyword === "afib" || keyword === "af") {
                  return "Atrial Fibrillation"
                } else if (keyword === "atrial flutter") {
                  return "Atrial Flutter"
                } else if (keyword === "bradycardia" || keyword.includes("sinus brady")) {
                  return "Sinus Bradycardia"
                } else if (keyword === "tachycardia" || keyword.includes("sinus tachy")) {
                  return "Sinus Tachycardia"
                } else if (keyword.includes("mobitz") && keyword.includes("type ii")) {
                  return "Second-degree AV Block, Mobitz Type II"
                } else if (keyword.includes("first") && keyword.includes("av block")) {
                  return "First-degree AV Block"
                } else if (keyword.includes("complete") || keyword.includes("third")) {
                  return "Complete Heart Block"
                } else if (keyword.includes("ventricular tachycardia")) {
                  return "Ventricular Tachycardia"
                } else if (keyword.includes("pvc") || keyword.includes("premature ventricular")) {
                  return "Premature Ventricular Contractions"
                }

                // Extract the rhythm phrase
                const rhythmPhrase = extractRhythmPhrase(lowerValue, keyword)
                if (rhythmPhrase) {
                  return capitalizeFirstLetters(rhythmPhrase)
                }
              }
            }
          }
        }
      }
    }

    // Default value if no rhythm information is found
    mappingPaths.Rhythm = "Not available in patient data"
    return "Regularly Regular"
  } catch (error) {
    console.error("Error in extractPulseRhythm:", error)
    mappingPaths.Rhythm = "Error in extraction"
    return "Regularly Regular"
  }
}

// Helper function to extract rhythm phrase from text
function extractRhythmPhrase(text, keyword) {
  try {
    const index = text.indexOf(keyword)
    if (index !== -1) {
      // Try to extract a phrase containing the keyword
      const start = Math.max(0, index - 10)
      const end = Math.min(text.length, index + keyword.length + 30)
      const phrase = text.substring(start, end)

      // Try to find sentence boundaries
      const sentenceMatch = phrase.match(new RegExp(`[^.!?]*\\b(${keyword})\\b[^.!?]*[.!?]`, "i"))
      if (sentenceMatch) {
        return sentenceMatch[0].trim()
      }

      return keyword
    }
    return null
  } catch (error) {
    console.error("Error in extractRhythmPhrase:", error)
    return null
  }
}

// Helper function to capitalize first letters of words
function capitalizeFirstLetters(str) {
  if (!str) return ""
  return str.replace(/\b\w/g, (char) => char.toUpperCase())
}

function extractPhysicalActivity(patientData, mappingPaths) {
  try {
    // Check in sequential order: lifestyle_factors, clinical_notes, progress_notes
    // 1. Check lifestyle_factors first
    if (patientData.lifestyle_factors?.physical_activity) {
      mappingPaths.PhysicalActivity = "lifestyle_factors>physical_activity"
      const activity = getStringValue(patientData.lifestyle_factors.physical_activity).toLowerCase()

      if (activity === "active" || activity === "moderate" || activity === "regular") {
        return "Yes"
      } else if (activity === "sedentary" || activity === "minimal" || activity === "limited") {
        return "No"
      }

      return patientData.lifestyle_factors.physical_activity
    }

    // 2. Check clinical_notes
    if (patientData.clinical_notes?.physical_activity) {
      mappingPaths.PhysicalActivity = "clinical_notes>physical_activity"
      const activity = getStringValue(patientData.clinical_notes.physical_activity).toLowerCase()

      if (
        activity.includes("active") ||
        activity.includes("moderate") ||
        activity.includes("regular") ||
        activity.includes("exercise") ||
        activity.includes("workout")
      ) {
        return "Yes"
      } else if (
        activity.includes("sedentary") ||
        activity.includes("minimal") ||
        activity.includes("limited") ||
        activity.includes("no exercise")
      ) {
        return "No"
      }

      return getStringValue(patientData.clinical_notes.physical_activity)
    }

    // 3. Check progress_notes
    if (patientData.progress_notes?.physical_activity) {
      mappingPaths.PhysicalActivity = "progress_notes>physical_activity"
      const activity = getStringValue(patientData.progress_notes.physical_activity).toLowerCase()

      if (
        activity.includes("active") ||
        activity.includes("moderate") ||
        activity.includes("regular") ||
        activity.includes("exercise") ||
        activity.includes("workout")
      ) {
        return "Yes"
      } else if (
        activity.includes("sedentary") ||
        activity.includes("minimal") ||
        activity.includes("limited") ||
        activity.includes("no exercise")
      ) {
        return "No"
      }

      return getStringValue(patientData.progress_notes.physical_activity)
    }

    // Check for exercise-related terms in all notes
    const hasExerciseTerms = Object.values(patientData).some((section) => {
      if (typeof section === "object" && section !== null) {
        return Object.values(section).some((value) => {
          const valueStr = getStringValue(value)
          if (valueStr) {
            const lowerValue = valueStr.toLowerCase()
            return (
              lowerValue.includes("regular exercise") ||
              lowerValue.includes("physically active") ||
              lowerValue.includes("works out") ||
              lowerValue.includes("goes to gym")
            )
          }
          return false
        })
      }
      return false
    })

    if (hasExerciseTerms) {
      mappingPaths.PhysicalActivity = "found exercise-related terms in notes"
      return "Yes"
    }

    return null
  } catch (error) {
    console.error("Error in extractPhysicalActivity:", error)
    return null
  }
}

function extractSmoking(patientData, mappingPaths) {
  try {
    // Check in sequential order: lifestyle_factors, problem_list, clinical_notes, progress_notes
    // 1. Check lifestyle_factors first
    if (patientData.lifestyle_factors?.tobacco_use) {
      mappingPaths.Smoke = "lifestyle_factors>tobacco_use"
      return patientData.lifestyle_factors.tobacco_use
    }

    if (patientData.lifestyle_factors?.smoking) {
      mappingPaths.Smoke = "lifestyle_factors>smoking"
      const smoking = getStringValue(patientData.lifestyle_factors.smoking).toLowerCase()

      if (smoking.includes("current") || smoking.includes("yes")) {
        return "Yes"
      } else if (smoking.includes("former") || smoking.includes("quit")) {
        return "Past"
      } else if (smoking.includes("no") || smoking.includes("never")) {
        return "No"
      }

      return patientData.lifestyle_factors.smoking
    }

    // 2. Check problem_list
    if (patientData.problem_list?.Smoking) {
      mappingPaths.Smoke = "problem_list>Smoking"
      const smoking = getStringValue(patientData.problem_list.Smoking).toLowerCase()

      if (smoking.includes("current") || smoking.includes("yes") || smoking.includes("active")) {
        return "Yes"
      } else if (smoking.includes("former") || smoking.includes("quit")) {
        return "Past"
      } else if (smoking.includes("no") || smoking.includes("never") || smoking.includes("ruled out")) {
        return "No"
      }

      return getStringValue(patientData.problem_list.Smoking)
    }

    if (patientData.problem_list?.smoking) {
      mappingPaths.Smoke = "problem_list>smoking"
      const smoking = getStringValue(patientData.problem_list.smoking).toLowerCase()

      if (smoking.includes("current") || smoking.includes("yes")) {
        return "Yes"
      } else if (smoking.includes("former") || smoking.includes("quit")) {
        return "Past"
      } else if (smoking.includes("no") || smoking.includes("never")) {
        return "No"
      }

      return getStringValue(patientData.problem_list.smoking)
    }

    if (patientData.problem_list?.["Tobacco Use"]) {
      mappingPaths.Smoke = "problem_list>Tobacco Use"
      const status = getStringValue(patientData.problem_list["Tobacco Use"])
      if (status === "No" || status === "Ruled out" || status === "Inactive") {
        return "No"
      } else if (status === "Yes" || status === "Active") {
        return "Yes"
      }
      return status
    }

    if (patientData.problem_list?.tobacco_use) {
      mappingPaths.Smoke = "problem_list>tobacco_use"
      return patientData.problem_list.tobacco_use
    }

    // 3. Check clinical_notes
    if (patientData.clinical_notes?.smoking) {
      mappingPaths.Smoke = "clinical_notes>smoking"
      const smoking = getStringValue(patientData.clinical_notes.smoking).toLowerCase()

      if (smoking.includes("current") || smoking.includes("yes") || smoking.includes("smoker")) {
        return "Yes"
      } else if (smoking.includes("former") || smoking.includes("quit") || smoking.includes("ex-smoker")) {
        return "Past"
      } else if (smoking.includes("no") || smoking.includes("never") || smoking.includes("non-smoker")) {
        return "No"
      }

      return getStringValue(patientData.clinical_notes.smoking)
    }

    if (patientData.clinical_notes?.tobacco_use) {
      mappingPaths.Smoke = "clinical_notes>tobacco_use"
      return getStringValue(patientData.clinical_notes.tobacco_use)
    }

    // 4. Check progress_notes
    if (patientData.progress_notes?.smoking) {
      mappingPaths.Smoke = "progress_notes>smoking"
      const smoking = getStringValue(patientData.progress_notes.smoking).toLowerCase()

      if (smoking.includes("current") || smoking.includes("yes") || smoking.includes("smoker")) {
        return "Yes"
      } else if (smoking.includes("former") || smoking.includes("quit") || smoking.includes("ex-smoker")) {
        return "Past"
      } else if (smoking.includes("no") || smoking.includes("never") || smoking.includes("non-smoker")) {
        return "No"
      }

      return getStringValue(patientData.progress_notes.smoking)
    }

    if (patientData.progress_notes?.tobacco_use) {
      mappingPaths.Smoke = "progress_notes>tobacco_use"
      return getStringValue(patientData.progress_notes.tobacco_use)
    }

    // 5. Check social_history
    if (patientData.social_history?.smoking_status) {
      mappingPaths.Smoke = "social_history>smoking_status"
      const smoking = getStringValue(patientData.social_history.smoking_status).toLowerCase()

      if (smoking.includes("current") || smoking.includes("yes") || smoking.includes("smoker")) {
        return "Yes"
      } else if (smoking.includes("former") || smoking.includes("quit") || smoking.includes("ex-smoker")) {
        return "Past"
      } else if (smoking.includes("no") || smoking.includes("never") || smoking.includes("non-smoker")) {
        return "No"
      }

      return getStringValue(patientData.social_history.smoking_status)
    }

    // 6. Check pulmonology_module
    if (patientData.pulmonology_module?.smoking_status) {
      mappingPaths.Smoke = "pulmonology_module>smoking_status"
      const smoking = getStringValue(patientData.pulmonology_module.smoking_status).toLowerCase()

      if (smoking.includes("current") || smoking.includes("yes") || smoking.includes("smoker")) {
        return "Yes"
      } else if (smoking.includes("former") || smoking.includes("quit") || smoking.includes("ex-smoker")) {
        return "Past"
      } else if (smoking.includes("no") || smoking.includes("never") || smoking.includes("non-smoker")) {
        return "No"
      }

      return getStringValue(patientData.pulmonology_module.smoking_status)
    }

    // Check for smoking-related terms in all notes
    const hasSmokingTerms = Object.values(patientData).some((section) => {
      if (typeof section === "object" && section !== null) {
        return Object.values(section).some((value) => {
          const valueStr = getStringValue(value)
          if (valueStr) {
            const lowerValue = valueStr.toLowerCase()
            return (
              lowerValue.includes("current smoker") ||
              lowerValue.includes("smokes cigarettes") ||
              lowerValue.includes("tobacco user")
            )
          }
          return false
        })
      }
      return false
    })

    if (hasSmokingTerms) {
      mappingPaths.Smoke = "found smoking-related terms in notes"
      return "Yes"
    }

    return "No" // Default to No if not found
  } catch (error) {
    console.error("Error in extractSmoking:", error)
    return "No"
  }
}

function extractTobacco(patientData, mappingPaths) {
  try {
    // Check in sequential order: lifestyle_factors, problem_list, clinical_notes, progress_notes
    // 1. Check lifestyle_factors first
    if (patientData.lifestyle_factors?.tobacco_use) {
      mappingPaths.Tobacco = "lifestyle_factors>tobacco_use"
      return patientData.lifestyle_factors.tobacco_use
    }

    // 2. Check problem_list
    if (patientData.problem_list?.["Tobacco Use"]) {
      mappingPaths.Tobacco = "problem_list>Tobacco Use"
      const status = getStringValue(patientData.problem_list["Tobacco Use"])
      if (status === "No" || status === "Ruled out" || status === "Inactive") {
        return "No"
      } else if (status === "Yes" || status === "Active") {
        return "Yes"
      }
      return status
    }

    if (patientData.problem_list?.tobacco_use) {
      mappingPaths.Tobacco = "problem_list>tobacco_use"
      return getStringValue(patientData.problem_list.tobacco_use)
    }

    // 3. Check clinical_notes
    if (patientData.clinical_notes?.tobacco_use) {
      mappingPaths.Tobacco = "clinical_notes>tobacco_use"
      return getStringValue(patientData.clinical_notes.tobacco_use)
    }

    // 4. Check progress_notes
    if (patientData.progress_notes?.tobacco_use) {
      mappingPaths.Tobacco = "progress_notes>tobacco_use"
      return getStringValue(patientData.progress_notes.tobacco_use)
    }

    // Check for tobacco-related terms in all notes
    const hasTobaccoTerms = Object.values(patientData).some((section) => {
      if (typeof section === "object" && section !== null) {
        return Object.values(section).some((value) => {
          const valueStr = getStringValue(value)
          if (valueStr) {
            const lowerValue = valueStr.toLowerCase()
            return (
              lowerValue.includes("chewing tobacco") ||
              lowerValue.includes("smokeless tobacco") ||
              lowerValue.includes("tobacco user")
            )
          }
          return false
        })
      }
      return false
    })

    if (hasTobaccoTerms) {
      mappingPaths.Tobacco = "found tobacco-related terms in notes"
      return "Yes"
    }

    return extractSmoking(patientData, mappingPaths) // Fall back to smoking data
  } catch (error) {
    console.error("Error in extractTobacco:", error)
    return "No"
  }
}

function extractDiet(patientData, mappingPaths) {
  try {
    // Check in sequential order: lifestyle_factors, clinical_notes, progress_notes
    // 1. Check lifestyle_factors first
    if (patientData.lifestyle_factors?.diet) {
      mappingPaths.Diet = "lifestyle_factors>diet"
      return patientData.lifestyle_factors.diet
    }

    // 2. Check clinical_notes
    if (patientData.clinical_notes?.diet) {
      mappingPaths.Diet = "clinical_notes>diet"
      return getStringValue(patientData.clinical_notes.diet)
    }

    // 3. Check progress_notes
    if (patientData.progress_notes?.diet) {
      mappingPaths.Diet = "progress_notes>diet"
      return getStringValue(patientData.progress_notes.diet)
    }

    // Check for diet-related terms in all notes
    const hasDietTerms = Object.values(patientData).some((section) => {
      if (typeof section === "object" && section !== null) {
        return Object.values(section).some((value) => {
          const valueStr = getStringValue(value)
          if (valueStr) {
            const lowerValue = valueStr.toLowerCase()
            if (
              lowerValue.includes("diet") ||
              lowerValue.includes("eating habits") ||
              lowerValue.includes("nutrition")
            ) {
              // Extract the diet information
              const dietMatch =
                lowerValue.match(/diet:?\s*([^.]+)/i) ||
                lowerValue.match(/eating habits:?\s*([^.]+)/i) ||
                lowerValue.match(/nutrition:?\s*([^.]+)/i)

              if (dietMatch && dietMatch[1]) {
                mappingPaths.Diet = "extracted from notes"
                return true
              }
            }
          }
          return false
        })
      }
      return false
    })

    if (hasDietTerms) {
      // This is just a placeholder - the actual diet value would be extracted in the loop above
      // but for simplicity, we're just returning a generic value here
      return "Found in notes"
    }

    return null
  } catch (error) {
    console.error("Error in extractDiet:", error)
    return null
  }
}

function extractAlcohol(patientData, mappingPaths) {
  try {
    // Check in sequential order: lifestyle_factors, clinical_notes, progress_notes
    // 1. Check lifestyle_factors first
    if (patientData.lifestyle_factors?.alcohol) {
      mappingPaths.Alcohol = "lifestyle_factors>alcohol"
      const alcoholValue = getStringValue(patientData.lifestyle_factors.alcohol).toLowerCase()

      if (
        alcoholValue.includes("frequently") ||
        alcoholValue.includes("daily") ||
        alcoholValue.includes("regular") ||
        alcoholValue.includes("heavy")
      ) {
        return "Current"
      } else if (
        alcoholValue.includes("occasionally") ||
        alcoholValue.includes("moderate") ||
        alcoholValue.includes("social")
      ) {
        return "Moderate"
      } else if (alcoholValue.includes("former")) {
        return "Past"
      } else if (
        alcoholValue.includes("no") ||
        alcoholValue.includes("never") ||
        alcoholValue.includes("rare") ||
        alcoholValue.includes("rarely")
      ) {
        return "No"
      }

      return patientData.lifestyle_factors.alcohol
    }

    // 2. Check clinical_notes
    if (patientData.clinical_notes?.alcohol) {
      mappingPaths.Alcohol = "clinical_notes>alcohol"
      const alcoholValue = getStringValue(patientData.clinical_notes.alcohol).toLowerCase()

      if (
        alcoholValue.includes("frequently") ||
        alcoholValue.includes("daily") ||
        alcoholValue.includes("regular") ||
        alcoholValue.includes("heavy")
      ) {
        return "Current"
      } else if (
        alcoholValue.includes("occasionally") ||
        alcoholValue.includes("moderate") ||
        alcoholValue.includes("social")
      ) {
        return "Moderate"
      } else if (alcoholValue.includes("former")) {
        return "Past"
      } else if (
        alcoholValue.includes("no") ||
        alcoholValue.includes("never") ||
        alcoholValue.includes("rare") ||
        alcoholValue.includes("rarely")
      ) {
        return "No"
      }

      return getStringValue(patientData.clinical_notes.alcohol)
    }

    // 3. Check progress_notes
    if (patientData.progress_notes?.alcohol) {
      mappingPaths.Alcohol = "progress_notes>alcohol"
      const alcoholValue = getStringValue(patientData.progress_notes.alcohol).toLowerCase()

      if (
        alcoholValue.includes("frequently") ||
        alcoholValue.includes("daily") ||
        alcoholValue.includes("regular") ||
        alcoholValue.includes("heavy")
      ) {
        return "Current"
      } else if (
        alcoholValue.includes("occasionally") ||
        alcoholValue.includes("moderate") ||
        alcoholValue.includes("social")
      ) {
        return "Moderate"
      } else if (alcoholValue.includes("former")) {
        return "Past"
      } else if (
        alcoholValue.includes("no") ||
        alcoholValue.includes("never") ||
        alcoholValue.includes("rare") ||
        alcoholValue.includes("rarely")
      ) {
        return "No"
      }

      return getStringValue(patientData.progress_notes.alcohol)
    }

    // 4. Check social_history
    if (patientData.social_history?.alcohol_use) {
      mappingPaths.Alcohol = "social_history>alcohol_use"
      const alcoholValue = getStringValue(patientData.social_history.alcohol_use).toLowerCase()

      if (
        alcoholValue.includes("frequently") ||
        alcoholValue.includes("daily") ||
        alcoholValue.includes("regular") ||
        alcoholValue.includes("heavy")
      ) {
        return "Current"
      } else if (
        alcoholValue.includes("occasionally") ||
        alcoholValue.includes("moderate") ||
        alcoholValue.includes("social")
      ) {
        return "Moderate"
      } else if (alcoholValue.includes("former")) {
        return "Past"
      } else if (
        alcoholValue.includes("no") ||
        alcoholValue.includes("never") ||
        alcoholValue.includes("rare") ||
        alcoholValue.includes("rarely")
      ) {
        return "No"
      }

      return getStringValue(patientData.social_history.alcohol_use)
    }

    // Check for alcohol-related terms in all notes
    const hasAlcoholTerms = Object.values(patientData).some((section) => {
      if (typeof section === "object" && section !== null) {
        return Object.values(section).some((value) => {
          const valueStr = getStringValue(value)
          if (valueStr) {
            const lowerValue = valueStr.toLowerCase()
            return (
              lowerValue.includes("alcohol consumption") ||
              lowerValue.includes("drinks alcohol") ||
              lowerValue.includes("etoh")
            )
          }
          return false
        })
      }
      return false
    })

    if (hasAlcoholTerms) {
      mappingPaths.Alcohol = "found alcohol-related terms in notes"
      return "Current" // Default to Current if found but frequency not specified
    }

    return null
  } catch (error) {
    console.error("Error in extractAlcohol:", error)
    return null
  }
}

function extractHypertension(patientData, mappingPaths) {
  try {
    // Check for explicit "No" values first in all possible locations
    if (patientData.problem_list?.hypertension?.status === "No") {
      mappingPaths.Hypertension = "problem_list>hypertension>status"
      return "No"
    }

    if (
      patientData.problem_list?.Hypertension?.status === "No" ||
      patientData.problem_list?.Hypertension?.status === "Ruled out"
    ) {
      mappingPaths.Hypertension = "problem_list>Hypertension>status"
      return "No"
    }

    if (patientData.visit_diagnosis?.hypertension === "No") {
      mappingPaths.Hypertension = "visit_diagnosis>hypertension"
      return "No"
    }

    if (patientData.clinical_notes?.hypertension === "No") {
      mappingPaths.Hypertension = "clinical_notes>hypertension"
      return "No"
    }

    if (patientData.progress_notes?.hypertension === "No") {
      mappingPaths.Hypertension = "progress_notes>hypertension"
      return "No"
    }

    // Check for "Yes" values in all possible locations
    if (
      patientData.problem_list?.hypertension?.status === "Yes" ||
      patientData.problem_list?.hypertension?.status === "Active"
    ) {
      mappingPaths.Hypertension = "problem_list>hypertension>status"
      return "Yes"
    }

    if (
      patientData.problem_list?.Hypertension?.status === "Yes" ||
      patientData.problem_list?.Hypertension?.status === "Active"
    ) {
      mappingPaths.Hypertension = "problem_list>Hypertension>status"
      return "Yes"
    }

    if (patientData.problem_list?.["Essential Hypertension"]?.status === "Active") {
      mappingPaths.Hypertension = "problem_list>Essential Hypertension>status"
      return "Yes"
    }

    if (patientData.visit_diagnosis?.hypertension === "Yes") {
      mappingPaths.Hypertension = "visit_diagnosis>hypertension"
      return "Yes"
    }

    if (patientData.clinical_notes?.hypertension === "Yes") {
      mappingPaths.Hypertension = "clinical_notes>hypertension"
      return "Yes"
    }

    if (patientData.progress_notes?.hypertension === "Yes") {
      mappingPaths.Hypertension = "progress_notes>hypertension"
      return "Yes"
    }

    // Check if on hypertension medications
    if (
      patientData.medication_administration_records?.hypertension_medications?.length > 0 ||
      patientData.medication_administration_records?.Hypertension?.length > 0
    ) {
      mappingPaths.Hypertension = "medication_administration_records>hypertension_medications"
      return "Yes"
    }

    // Check blood pressure values
    const systolic = extractSystolic(patientData, {})
    const diastolic = extractDiastolic(patientData, {})

    if ((systolic && systolic > 140) || (diastolic && diastolic > 90)) {
      mappingPaths.Hypertension = "elevated blood pressure values"
      return "Yes"
    }

    // If no hypertension indicators found, set the mapping path and return "No"
    mappingPaths.Hypertension = "not found"
    return "No"
  } catch (error) {
    console.error("Error in extractHypertension:", error)
    return "No"
  }
}

function extractHeartRelatedSymptom(patientData, mappingPaths) {
  try {
    // Check for explicit "No" values first in all possible locations
    if (patientData.problem_list?.heart_related_symptoms === "No") {
      mappingPaths.HeartRelatedSymptom = "problem_list>heart_related_symptoms"
      return "No"
    }

    if (patientData.visit_diagnosis?.heart_related_symptoms === "No") {
      mappingPaths.HeartRelatedSymptom = "visit_diagnosis>heart_related_symptoms"
      return "No"
    }

    if (patientData.clinical_notes?.heart_related_symptoms === "No") {
      mappingPaths.HeartRelatedSymptom = "clinical_notes>heart_related_symptoms"
      return "No"
    }

    // Check for heart-related symptoms in various locations
    if (patientData.problem_list?.heart_related_symptoms === "Yes") {
      mappingPaths.HeartRelatedSymptom = "problem_list>heart_related_symptoms"
      return "Yes"
    }

    // Check clinical notes for CAD
    if (patientData.clinical_notes?.coronary_artery_disease) {
      mappingPaths.HeartRelatedSymptom = "clinical_notes>coronary_artery_disease"
      const cadNote = getStringValue(patientData.clinical_notes.coronary_artery_disease)
      if (cadNote && !cadNote.toLowerCase().includes("no evidence")) {
        return "Yes"
      }
    }

    // Check progress notes for heart disease
    if (patientData.progress_notes?.heart_disease) {
      mappingPaths.HeartRelatedSymptom = "progress_notes>heart_disease"
      return "Yes"
    }

    // Check for chest pain or angina in various fields
    const hasChestPain = Object.values(patientData).some((section) => {
      if (typeof section === "object" && section !== null) {
        return Object.values(section).some((value) => {
          const valueStr = getStringValue(value)
          if (valueStr) {
            const lowerValue = valueStr.toLowerCase()
            return lowerValue.includes("chest pain") || lowerValue.includes("angina")
          }
          return false
        })
      }
      return false
    })

    if (hasChestPain) {
      mappingPaths.HeartRelatedSymptom = "found 'chest pain' or 'angina' in notes"
      return "Yes"
    }

    // If no heart-related symptoms found, set the mapping path and return "No"
    mappingPaths.HeartRelatedSymptom = "no heart-related symptoms found"
    return "No"
  } catch (error) {
    console.error("Error in extractHeartRelatedSymptom:", error)
    return "No"
  }
}

function extractPrevHeartDisease(patientData, mappingPaths) {
  try {
    // Check for explicit "No" values first in all possible locations
    if (
      patientData.problem_list?.["Heart Disease"]?.status === "No" ||
      patientData.problem_list?.["Heart Disease"]?.status === "Ruled out"
    ) {
      mappingPaths.PrevHeartDisease = "problem_list>Heart Disease>status"
      return "No"
    }

    if (patientData.problem_list?.heart_disease?.status === "No") {
      mappingPaths.PrevHeartDisease = "problem_list>heart_disease>status"
      return "No"
    }

    if (patientData.visit_diagnosis?.heart_disease === "No") {
      mappingPaths.PrevHeartDisease = "visit_diagnosis>heart_disease"
      return "No"
    }

    if (patientData.visit_diagnosis?.coronary_artery_disease === "No") {
      mappingPaths.PrevHeartDisease = "visit_diagnosis>coronary_artery_disease"
      return "No"
    }

    if (
      patientData.problem_list?.["Coronary Artery Disease"]?.status === "No" ||
      patientData.problem_list?.["Coronary Artery Disease"]?.status === "Ruled out"
    ) {
      mappingPaths.PrevHeartDisease = "problem_list>Coronary Artery Disease>status"
      return "No"
    }

    if (patientData.problem_list?.coronary_artery_disease?.status === "No") {
      mappingPaths.PrevHeartDisease = "problem_list>coronary_artery_disease>status"
      return "No"
    }

    // Check problem_list first
    if (
      patientData.problem_list?.["Heart Disease"]?.status === "Yes" ||
      patientData.problem_list?.["Heart Disease"]?.status === "Active"
    ) {
      mappingPaths.PrevHeartDisease = "problem_list>Heart Disease>status"
      return "Yes"
    }

    if (
      patientData.problem_list?.heart_disease?.status === "Yes" ||
      patientData.problem_list?.heart_disease?.status === "Active"
    ) {
      mappingPaths.PrevHeartDisease = "problem_list>heart_disease>status"
      return "Yes"
    }

    // Check for CAD in problem list or visit diagnosis
    if (
      patientData.problem_list?.["Coronary Artery Disease"]?.status === "Yes" ||
      patientData.problem_list?.["Coronary Artery Disease"]?.status === "Active" ||
      patientData.problem_list?.coronary_artery_disease?.status === "Yes" ||
      patientData.problem_list?.coronary_artery_disease?.status === "Active" ||
      patientData.visit_diagnosis?.coronary_artery_disease === "Yes"
    ) {
      mappingPaths.PrevHeartDisease = "problem_list/visit_diagnosis>coronary_artery_disease"
      return "Yes"
    }

    // Check clinical notes for CAD
    if (patientData.clinical_notes?.coronary_artery_disease) {
      const cadNote = getStringValue(patientData.clinical_notes.coronary_artery_disease)
      if (cadNote && !cadNote.toLowerCase().includes("no evidence")) {
        mappingPaths.HeartRelatedSymptom = "clinical_notes>coronary_artery_disease"
        return "Yes"
      }
    }

    // Check clinical notes for heart disease
    if (patientData.clinical_notes?.heart_disease) {
      const hdNote = getStringValue(patientData.clinical_notes.heart_disease)
      if (hdNote && !hdNote.toLowerCase().includes("no evidence")) {
        mappingPaths.PrevHeartDisease = "clinical_notes>heart_disease"
        return "Yes"
      }
    }

    // Check cardiology module
    if (patientData.cardiology_module?.coronary_artery_disease) {
      const cadNote = getStringValue(patientData.cardiology_module.coronary_artery_disease)
      if (cadNote && !cadNote.toLowerCase().includes("no evidence")) {
        mappingPaths.PrevHeartDisease = "cardiology_module>coronary_artery_disease"
        return "Yes"
      }
    }

    // Check if on heart disease medications
    if (
      patientData.medication_administration_records?.heart_disease_medications?.length > 0 ||
      patientData.medication_administration_records?.["Heart Disease"]?.length > 0
    ) {
      mappingPaths.PrevHeartDisease = "medication_administration_records>heart_disease_medications"
      return "Yes"
    }

    // If no previous heart disease found, set the mapping path and return "No"
    mappingPaths.PrevHeartDisease = "no previous heart disease found"
    return "No"
  } catch (error) {
    console.error("Error in extractPrevHeartDisease:", error)
    return "No"
  }
}

function extractFamilyHistoryofHeartDisease(patientData, mappingPaths) {
  try {
    // Check for explicit "No" values first in all possible locations
    if (patientData.family_history?.heart_disease === "No") {
      mappingPaths.FamilyHistoryofHeartDisease = "family_history>heart_disease"
      return "No"
    }

    if (patientData.family_history?.coronary_artery_disease === "No") {
      mappingPaths.FamilyHistoryofHeartDisease = "family_history>coronary_artery_disease"
      return "No"
    }

    if (patientData.family_history?.hypertension === "No") {
      mappingPaths.FamilyHistoryofHeartDisease = "family_history>hypertension"
      return "No"
    }

    // Check for "Yes" values in all possible locations
    if (patientData.family_history?.heart_disease === "Yes") {
      mappingPaths.FamilyHistoryofHeartDisease = "family_history>heart_disease"
      return "Yes"
    }

    // Check for heart disease in family history
    if (patientData.family_history?.heart_disease) {
      if (
        typeof patientData.family_history.heart_disease === "object" &&
        patientData.family_history.heart_disease.relation
      ) {
        mappingPaths.FamilyHistoryofHeartDisease = "family_history>heart_disease>relation"
        return "Yes"
      }
    }

    // Check for hypertension in family history as it's a risk factor
    if (patientData.family_history?.hypertension === "Yes") {
      mappingPaths.FamilyHistoryofHeartDisease = "family_history>hypertension"
      return "Yes"
    }

    if (patientData.family_history?.hypertension) {
      if (
        typeof patientData.family_history.hypertension === "object" &&
        patientData.family_history.hypertension.relation
      ) {
        mappingPaths.FamilyHistoryofHeartDisease = "family_history>hypertension>relation"
        return "Yes"
      }
    }

    // Check for coronary artery disease in family history
    if (patientData.family_history?.coronary_artery_disease === "Yes") {
      mappingPaths.FamilyHistoryofHeartDisease = "family_history>coronary_artery_disease"
      return "Yes"
    }

    if (patientData.family_history?.coronary_artery_disease) {
      if (
        typeof patientData.family_history.coronary_artery_disease === "object" &&
        patientData.family_history.coronary_artery_disease.relation
      ) {
        mappingPaths.FamilyHistoryofHeartDisease = "family_history>coronary_artery_disease>relation"
        return "Yes"
      }
    }

    // Check for myocardial infarction in family history
    if (patientData.family_history?.myocardial_infarction === "Yes") {
      mappingPaths.FamilyHistoryofHeartDisease = "family_history>myocardial_infarction"
      return "Yes"
    }

    if (patientData.family_history?.myocardial_infarction) {
      if (
        typeof patientData.family_history.myocardial_infarction === "object" &&
        patientData.family_history.myocardial_infarction.relation
      ) {
        mappingPaths.FamilyHistoryofHeartDisease = "family_history>myocardial_infarction>relation"
        return "Yes"
      }
    }

    // If no family history of heart disease found, set the mapping path and return "No"
    mappingPaths.FamilyHistoryofHeartDisease = "no family history of heart disease found"
    return "No"
  } catch (error) {
    console.error("Error in extractFamilyHistoryofHeartDisease:", error)
    return "No"
  }
}

function extractDiabetes(patientData, mappingPaths) {
  try {
    // Check for explicit "No" values first in all possible locations
    if (
      patientData.problem_list?.["Diabetes Mellitus"]?.status === "No" ||
      patientData.problem_list?.["Diabetes Mellitus"]?.status === "Ruled out"
    ) {
      mappingPaths.DiabetesMellitus = "problem_list>Diabetes Mellitus>status"
      return "No"
    }

    if (patientData.problem_list?.diabetes_mellitus?.status === "No") {
      mappingPaths.DiabetesMellitus = "problem_list>diabetes_mellitus>status"
      return "No"
    }

    if (patientData.visit_diagnosis?.diabetes_mellitus === "No") {
      mappingPaths.DiabetesMellitus = "visit_diagnosis>diabetes_mellitus"
      return "No"
    }

    if (patientData.clinical_notes?.diabetes_mellitus === "No") {
      mappingPaths.DiabetesMellitus = "clinical_notes>diabetes_mellitus"
      return "No"
    }

    if (patientData.progress_notes?.diabetes_mellitus === "No") {
      mappingPaths.DiabetesMellitus = "progress_notes>diabetes_mellitus"
      return "No"
    }

    // Check various locations for diabetes information
    // Check problem_list
    if (patientData.problem_list?.["Diabetes Mellitus Type 2"]?.status === "Active") {
      mappingPaths.DiabetesMellitus = "problem_list>Diabetes Mellitus Type 2>status"

      // Check HbA1c to determine if controlled
      const hba1c = getHbA1cValue(patientData)

      if (hba1c && hba1c >= 7.5) {
        return "Not controlled with medication"
      } else {
        return "Controlled with medication"
      }
    }

    if (
      patientData.problem_list?.["Diabetes Mellitus"]?.status === "Yes" ||
      patientData.problem_list?.["Diabetes Mellitus"]?.status === "Active"
    ) {
      mappingPaths.DiabetesMellitus = "problem_list>Diabetes Mellitus>status"

      // Check HbA1c to determine if controlled
      const hba1c = getHbA1cValue(patientData)

      if (hba1c && hba1c >= 7.5) {
        return "Not controlled with medication"
      } else {
        return "Controlled with medication"
      }
    }

    if (
      patientData.problem_list?.diabetes_mellitus?.status === "Yes" ||
      patientData.problem_list?.diabetes_mellitus?.status === "Active"
    ) {
      mappingPaths.DiabetesMellitus = "problem_list>diabetes_mellitus>status"

      // Check HbA1c to determine if controlled
      const hba1c = getHbA1cValue(patientData)

      if (hba1c && hba1c >= 7.5) {
        return "Not controlled with medication"
      } else {
        return "Controlled with medication"
      }
    }

    // Check visit_diagnosis
    if (patientData.visit_diagnosis?.diagnoses) {
      const hasDiabetes = patientData.visit_diagnosis.diagnoses.some(
        (diagnosis) => diagnosis.name === "Diabetes Mellitus" && diagnosis.status === "Yes",
      )

      if (hasDiabetes) {
        mappingPaths.DiabetesMellitus = "visit_diagnosis>diagnoses>Diabetes Mellitus"

        // Check HbA1c to determine if controlled
        const hba1c = getHbA1cValue(patientData)

        if (hba1c && hba1c >= 7.5) {
          return "Not controlled with medication"
        } else {
          return "Controlled with medication"
        }
      }
    }

    if (patientData.visit_diagnosis?.diabetes_mellitus === "Yes") {
      mappingPaths.DiabetesMellitus = "visit_diagnosis>diabetes_mellitus"

      // Check HbA1c to determine if controlled
      const hba1c = getHbA1cValue(patientData)

      if (hba1c && hba1c >= 7.5) {
        return "Not controlled with medication"
      } else {
        return "Controlled with medication"
      }
    }

    // Check clinical notes
    if (patientData.clinical_notes?.diabetes_mellitus) {
      const dmNote = getStringValue(patientData.clinical_notes.diabetes_mellitus).toLowerCase()
      if (dmNote && !dmNote.includes("no")) {
        mappingPaths.DiabetesMellitus = "clinical_notes>diabetes_mellitus"

        // Check HbA1c to determine if controlled
        const hba1c = getHbA1cValue(patientData)

        if (hba1c && hba1c >= 7.5) {
          return "Not controlled with medication"
        } else {
          return "Controlled with medication"
        }
      }
    }

    // Check if on diabetes medications
    if (
      patientData.medication_administration_records?.diabetes_mellitus_medications?.length > 0 ||
      patientData.medication_administration_records?.["Diabetes Mellitus"]?.length > 0
    ) {
      mappingPaths.DiabetesMellitus = "medication_administration_records>diabetes_mellitus_medications"

      // Check HbA1c to determine if controlled
      const hba1c = getHbA1cValue(patientData)

      if (hba1c && hba1c >= 7.5) {
        return "Not controlled with medication"
      } else {
        return "Controlled with medication"
      }
    }

    // Check HbA1c and blood glucose values
    const hba1c = getHbA1cValue(patientData)
    const bloodGlucose = getBloodGlucoseValue(patientData)

    if (hba1c && hba1c >= 6.5) {
      mappingPaths.DiabetesMellitus = "elevated HbA1c"
      return "Not controlled with medication"
    }

    if (hba1c && hba1c >= 5.7 && hba1c < 6.5) {
      mappingPaths.DiabetesMellitus = "elevated HbA1c (pre-diabetic range)"
      return "Pre-diabetic"
    }

    if (bloodGlucose && bloodGlucose >= 126) {
      mappingPaths.DiabetesMellitus = "elevated blood glucose"
      return "Not controlled with medication"
    }

    // If no diabetes indicators found, set the mapping path and return "No"
    mappingPaths.DiabetesMellitus = "not found"
    return "No"
  } catch (error) {
    console.error("Error in extractDiabetes:", error)
    return "No"
  }
}

// Helper function to get HbA1c value from various locations
function getHbA1cValue(patientData) {
  try {
    let hba1cValue = null

    // Check lab_results
    if (patientData.lab_results?.hba1c) {
      if (typeof patientData.lab_results.hba1c === "object" && patientData.lab_results.hba1c.value) {
        const match = patientData.lab_results.hba1c.value.toString().match(/(\d+(\.\d+)?)/)
        if (match) {
          hba1cValue = Number.parseFloat(match[1])
        }
      } else {
        const match = patientData.lab_results.hba1c.toString().match(/(\d+(\.\d+)?)/)
        if (match) {
          hba1cValue = Number.parseFloat(match[1])
        }
      }
    }

    // Check flowsheets if not found in lab_results
    if (!hba1cValue && patientData.flowsheets?.hba1c) {
      if (typeof patientData.flowsheets.hba1c === "object" && patientData.flowsheets.hba1c.value) {
        const match = patientData.flowsheets.hba1c.value.toString().match(/(\d+(\.\d+)?)/)
        if (match) {
          hba1cValue = Number.parseFloat(match[1])
        }
      } else {
        const match = patientData.flowsheets.hba1c.toString().match(/(\d+(\.\d+)?)/)
        if (match) {
          hba1cValue = Number.parseFloat(match[1])
        }
      }
    }

    return hba1cValue
  } catch (error) {
    console.error("Error in getHbA1cValue:", error)
    return null
  }
}

// Helper function to get blood glucose value
function getBloodGlucoseValue(patientData) {
  try {
    let bloodGlucoseValue = null

    if (patientData.lab_results?.blood_glucose) {
      if (typeof patientData.lab_results.blood_glucose === "object" && patientData.lab_results.blood_glucose.value) {
        const match = patientData.lab_results.blood_glucose.value.toString().match(/(\d+(\.\d+)?)/)
        if (match) {
          bloodGlucoseValue = Number.parseFloat(match[1])
        }
      } else {
        const match = patientData.lab_results.blood_glucose.toString().match(/(\d+(\.\d+)?)/)
        if (match) {
          bloodGlucoseValue = Number.parseFloat(match[1])
        }
      }
    }

    return bloodGlucoseValue
  } catch (error) {
    console.error("Error in getBloodGlucoseValue:", error)
    return null
  }
}

function extractDyslipidaemia(patientData, mappingPaths) {
  try {
    // Check for explicit "No" values first in all possible locations
    if (
      patientData.problem_list?.["Dyslipidemia"]?.status === "No" ||
      patientData.problem_list?.["Dyslipidemia"]?.status === "Ruled out"
    ) {
      mappingPaths.Dyslipidaemia = "problem_list>Dyslipidemia>status"
      return "No"
    }

    if (patientData.problem_list?.dyslipidemia?.status === "No") {
      mappingPaths.Dyslipidaemia = "problem_list>dyslipidemia>status"
      return "No"
    }

    if (patientData.visit_diagnosis?.dyslipidemia === "No") {
      mappingPaths.Dyslipidaemia = "visit_diagnosis>dyslipidemia"
      return "No"
    }

    if (patientData.clinical_notes?.dyslipidemia === "No") {
      mappingPaths.Dyslipidaemia = "clinical_notes>dyslipidemia"
      return "No"
    }

    if (patientData.progress_notes?.dyslipidemia === "No") {
      mappingPaths.Dyslipidaemia = "progress_notes>dyslipidemia"
      return "No"
    }

    // Check various locations for dyslipidemia information
    // Check problem_list
    if (
      patientData.problem_list?.["Dyslipidemia"]?.status === "Yes" ||
      patientData.problem_list?.["Dyslipidemia"]?.status === "Active"
    ) {
      mappingPaths.Dyslipidaemia = "problem_list>Dyslipidemia>status"
      return "Yes"
    }

    if (
      patientData.problem_list?.dyslipidemia?.status === "Yes" ||
      patientData.problem_list?.dyslipidemia?.status === "Active"
    ) {
      mappingPaths.Dyslipidaemia = "problem_list>dyslipidemia>status"
      return "Yes"
    }

    if (
      patientData.problem_list?.["Hyperlipidemia"]?.status === "Yes" ||
      patientData.problem_list?.["Hyperlipidemia"]?.status === "Active"
    ) {
      mappingPaths.Dyslipidaemia = "problem_list>Hyperlipidemia>status"
      return "Yes"
    }

    // Check visit_diagnosis
    if (patientData.visit_diagnosis?.diagnoses) {
      const hasDyslipidemia = patientData.visit_diagnosis.diagnoses.some(
        (diagnosis) => diagnosis.name === "Dyslipidemia" || diagnosis.name === "Hyperlipidemia",
      )

      if (hasDyslipidemia) {
        mappingPaths.Dyslipidaemia = "visit_diagnosis>diagnoses>Dyslipidemia"
        return "Yes"
      }
    }

    if (patientData.visit_diagnosis?.dyslipidemia === "Yes") {
      mappingPaths.Dyslipidaemia = "visit_diagnosis>dyslipidemia"
      return "Yes"
    }

    // Check progress notes
    if (patientData.progress_notes?.dyslipidemia) {
      mappingPaths.Dyslipidaemia = "progress_notes>dyslipidemia"
      return "Yes"
    }

    // Check for dyslipidemia in progress_notes
    if (patientData.progress_notes && patientData.progress_notes["2024-04-01"]?.dyslipidemia) {
      mappingPaths.Dyslipidaemia = "progress_notes>2024-04-01>dyslipidemia"
      return "Yes"
    }

    // Check clinical notes
    if (patientData.clinical_notes?.dyslipidemia) {
      mappingPaths.Dyslipidaemia = "clinical_notes>dyslipidemia"
      return "Yes"
    }

    // Check if on dyslipidemia medications
    if (
      patientData.medication_administration_records?.dyslipidemia_medications?.length > 0 ||
      patientData.medication_administration_records?.Dyslipidemia?.length > 0 ||
      patientData.medication_administration_records?.Hyperlipidemia?.length > 0
    ) {
      mappingPaths.Dyslipidaemia = "medication_administration_records>dyslipidemia_medications"
      return "Yes"
    }

    // Check lipid profile
    if (patientData.lab_results?.lipid_profile) {
      let lipidProfile
      if (typeof patientData.lab_results.lipid_profile === "object" && patientData.lab_results.lipid_profile.result) {
        lipidProfile = getStringValue(patientData.lab_results.lipid_profile.result).toLowerCase()
      } else {
        lipidProfile = getStringValue(patientData.lab_results.lipid_profile).toLowerCase()
      }

      if (lipidProfile.includes("high") || lipidProfile.includes("elevated") || lipidProfile.includes("abnormal")) {
        mappingPaths.Dyslipidaemia = "lab_results>lipid_profile"
        return "Yes"
      }
    }

    // If no dyslipidemia indicators found, set the mapping path and return "No"
    mappingPaths.Dyslipidaemia = "not found"
    return "No"
  } catch (error) {
    console.error("Error in extractDyslipidaemia:", error)
    return "No"
  }
}

function extractPsychologicalStress(patientData, mappingPaths) {
  try {
    // Check for explicit "No" values first in all possible locations
    if (patientData.social_history?.stressors === "No") {
      mappingPaths.PsychologicalStress = "social_history>stressors"
      return "No"
    }

    if (patientData.lifestyle_factors?.stress === "No") {
      mappingPaths.PsychologicalStress = "lifestyle_factors>stress"
      return "No"
    }

    if (patientData.clinical_notes?.stress === "No") {
      mappingPaths.PsychologicalStress = "clinical_notes>stress"
      return "No"
    }

    // 1. Check Social History / Lifestyle
    if (patientData.social_history?.stressors) {
      mappingPaths.PsychologicalStress = "social_history>stressors"
      return "Yes"
    }

    if (patientData.lifestyle_factors?.stress) {
      mappingPaths.PsychologicalStress = "lifestyle_factors>stress"
      return "Yes"
    }

    // 2. Check Clinical Notes / Progress Notes
    if (patientData.clinical_notes?.stress) {
      mappingPaths.PsychologicalStress = "clinical_notes>stress"
      return "Yes"
    }

    if (patientData.clinical_notes?.anxiety) {
      mappingPaths.PsychologicalStress = "clinical_notes>anxiety"
      return "Yes"
    }

    if (patientData.clinical_notes?.sleep_disturbances) {
      mappingPaths.PsychologicalStress = "clinical_notes>sleep_disturbances"
      return "Yes"
    }

    if (patientData.progress_notes?.stress) {
      mappingPaths.PsychologicalStress = "progress_notes>stress"
      return "Yes"
    }

    if (patientData.progress_notes?.anxiety) {
      mappingPaths.PsychologicalStress = "progress_notes>anxiety"
      return "Yes"
    }

    if (patientData.progress_notes && patientData.progress_notes["2024-04-01"]?.stress) {
      mappingPaths.PsychologicalStress = "progress_notes>2024-04-01>stress"
      return "Yes"
    }

    // 3. Check Mental Health / Behavioral Health Notes
    if (patientData.mental_health_notes) {
      mappingPaths.PsychologicalStress = "mental_health_notes"
      return "Yes"
    }

    if (patientData.behavioral_health_notes) {
      mappingPaths.PsychologicalStress = "behavioral_health_notes"
      return "Yes"
    }

    // 4. Check Problem List for stress-related diagnoses
    if (
      patientData.problem_list?.["Anxiety Disorder"]?.status === "Active" ||
      patientData.problem_list?.["Depression"]?.status === "Active" ||
      patientData.problem_list?.["Panic Disorder"]?.status === "Active" ||
      patientData.problem_list?.["Generalized Anxiety Disorder"]?.status === "Active" ||
      patientData.problem_list?.["Burnout Syndrome"]?.status === "Active" ||
      patientData.problem_list?.["Insomnia"]?.status === "Active"
    ) {
      mappingPaths.PsychologicalStress = "problem_list>stress_related_diagnosis"
      return "Yes"
    }

    // 5. Check Counseling / Psychology / Psychiatry Notes
    if (patientData.counseling_notes) {
      mappingPaths.PsychologicalStress = "counseling_notes"
      return "Yes"
    }

    if (patientData.psychology_notes) {
      mappingPaths.PsychologicalStress = "psychology_notes"
      return "Yes"
    }

    if (patientData.psychiatry_notes) {
      mappingPaths.PsychologicalStress = "psychiatry_notes"
      return "Yes"
    }

    // 6. Check Screening Assessments
    if (
      patientData.screening_assessments?.phq9 > 4 ||
      patientData.screening_assessments?.gad7 > 4 ||
      patientData.screening_assessments?.pcl5 > 30
    ) {
      mappingPaths.PsychologicalStress = "screening_assessments>phq9/gad7/pcl5"
      return "Yes"
    }

    // Check medications for anxiety or depression medications
    if (
      patientData.medication_administration_records?.anxiety_medications?.length > 0 ||
      patientData.medication_administration_records?.depression_medications?.length > 0 ||
      patientData.medication_administration_records?.["Anxiety Disorder"]?.length > 0 ||
      patientData.medication_administration_records?.["Depression"]?.length > 0
    ) {
      mappingPaths.PsychologicalStress = "medication_administration_records>mental_health_medications"
      return "Yes"
    }

    // Check for stress-related terms in all notes
    const stressTerms = [
      "stress",
      "anxiety",
      "depression",
      "mental health issues",
      "psychological distress",
      "job-related stress",
      "family issues",
      "sleep disturbances",
      "feeling overwhelmed",
      "burnout",
      "panic attack",
      "worried",
      "nervous",
      "tension",
    ]

    const hasStressTerms = Object.values(patientData).some((section) => {
      if (typeof section === "object" && section !== null) {
        return Object.values(section).some((value) => {
          const valueStr = getStringValue(value)
          if (valueStr) {
            const lowerValue = valueStr.toLowerCase()
            return stressTerms.some((term) => lowerValue.includes(term))
          }
          return false
        })
      }
      return false
    })

    if (hasStressTerms) {
      mappingPaths.PsychologicalStress = "found stress-related terms in notes"
      return "Yes"
    }

    // If no stress indicators found, set the mapping path and return "No"
    mappingPaths.PsychologicalStress = "no stress indicators found"
    return "No"
  } catch (error) {
    console.error("Error in extractPsychologicalStress:", error)
    return "No"
  }
}
